import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  Input,
  Textarea,
  StatusBadge,
  Badge,
  Modal,
  uiCurrency,
} from '@besiktning/ui'
import { 
  ArrowLeft, 
  Car, 
  AlertTriangle,
  Send,
  FileImage,
  Sparkles
} from 'lucide-react'
import {
  garageApi,
  apiErrorMessage,
  formatDateTime,
  formatCurrency,
  computeTaxBreakdownFromGross,
  TaxBreakdownSummary,
} from '@besiktning/ui'
import { SERVICE_CATEGORIES } from '@besiktning/ui'
import type { ExtraService, Quote, QuoteLineItem } from '@besiktning/ui'
import { t } from '../i18n'
import WorkOrderEditor, { lineItemsTotal } from '../components/WorkOrderEditor'
import type { LineItemForm } from '../components/WorkOrderEditor'

function categoryLabel(category: string): string {
  return SERVICE_CATEGORIES.find((c) => c.value === category)?.label ?? category
}

/** One quote row per requested add-on, priced by the garage. */
function extrasAsLineItems(extras: ExtraService[]): LineItemForm[] {
  return extras.map((extra) => ({
    description: extra.note?.trim()
      ? `${categoryLabel(extra.category)} – ${extra.note.trim()}`
      : categoryLabel(extra.category),
    quantity: 1,
    unit_price: 0,
    unit_type: 'st',
  }))
}

export default function RequestDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const [showQuoteModal, setShowQuoteModal] = useState(false)
  const [quotePrice, setQuotePrice] = useState('')
  const [quoteMessage, setQuoteMessage] = useState('')
  const [lineItems, setLineItems] = useState<LineItemForm[]>([])
  const [submitError, setSubmitError] = useState('')

  const { data: requestData, isLoading } = useQuery({
    queryKey: ['request', id],
    queryFn: () => garageApi.getRequest(id!),
    enabled: !!id,
  })
  
  const request = requestData as any
  const extraServices: ExtraService[] = request?.extra_services || []

  // Get all quotes for this request - quotes are already filtered by the backend
  // to only include quotes from this garage
  const allQuotes = request?.quotes || []
  
  // Check if garage already has a quote for this request
  // The first quote (if any) is from this garage since backend filters by garage
  const existingQuote = allQuotes.length > 0 ? allQuotes[0] : null

  const calculatedTotal = lineItemsTotal(lineItems)

  const priceTaxBreakdown = quotePrice
    ? computeTaxBreakdownFromGross(parseInt(quotePrice, 10) || 0)
    : null

  const submitQuoteMutation = useMutation({
    mutationFn: () => {
      const payload = {
        price: lineItems.length > 0 ? calculatedTotal : parseInt(quotePrice),
        message: quoteMessage,
        proposed_time_slots: [],
        // Always send the array so clearing every row also clears it server-side.
        line_items: lineItems.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          unit_type: item.unit_type,
        })),
      }

      // An existing pending quote is edited in place — POSTing again is rejected
      // as a duplicate quote for the request.
      return existingQuote
        ? garageApi.updateQuote(existingQuote.id, payload)
        : garageApi.submitQuote(id!, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request', id] })
      setShowQuoteModal(false)
      setQuotePrice('')
      setQuoteMessage('')
      setLineItems([])
      setSubmitError('')
    },
    onError: (error: unknown) => {
      setSubmitError(apiErrorMessage(error, t('requestDetail.errSubmitQuote')))
    },
  })

  const handleSubmitQuote = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError('')
    submitQuoteMutation.mutate()
  }

  const openQuoteModal = () => {
    if (existingQuote) {
      setQuotePrice(existingQuote.price.toString())
      setQuoteMessage(existingQuote.message || '')
      // Load existing line items if available
      if (existingQuote.line_items && existingQuote.line_items.length > 0) {
        setLineItems(existingQuote.line_items.map((item: QuoteLineItem) => ({
          id: item.id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          unit_type: item.unit_type,
        })))
      } else {
        setLineItems([])
      }
    } else {
      setQuotePrice('')
      setQuoteMessage('')
      // Start from the customer's add-ons so the garage only has to price them.
      setLineItems(extrasAsLineItems(extraServices))
    }
    setShowQuoteModal(true)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('requestDetail.notFound')}</p>
        <Button onClick={() => navigate('/requests')} className="mt-4">
          {t('requestDetail.backToRequests')}
        </Button>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center">
        <Button
          variant="ghost"
          onClick={() => navigate('/requests')}
          className="mr-4"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('requestDetail.title')}</h1>
          <p className="text-gray-600">
            {t('requestDetail.received', { date: formatDateTime(request.created_at) })}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {request.cancelled && <Badge variant="danger">{t('requestDetail.cancelled')}</Badge>}
          <StatusBadge status={request.status} />
        </div>
      </div>

      {/* Cancelled banner */}
      {request.cancelled && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-red-800">{t('requestDetail.cancelledTitle')}</p>
            <p className="text-sm text-red-700">
              {t('requestDetail.cancelledBody')}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Vehicle & Issues */}
        <div className="lg:col-span-2 space-y-6">
          {/* Vehicle Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Car className="mr-2 h-5 w-5" />
                {t('requestDetail.vehicleInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">{t('requestDetail.makeModel')}</p>
                  <p className="font-medium">
                    {request.vehicle?.make} {request.vehicle?.model}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('requestDetail.regNumber')}</p>
                  <p className="font-medium">{request.vehicle?.reg_number}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('requestDetail.year')}</p>
                  <p className="font-medium">{request.vehicle?.year || t('requestDetail.unknown')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('requestDetail.kommun')}</p>
                  <p className="font-medium">{request.kommun}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Requested add-ons */}
          {extraServices.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Sparkles className="mr-2 h-5 w-5 text-blue-600" />
                  {t('requestDetail.extraServices')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-sm text-gray-500">
                  {t('requestDetail.extraServicesHint')}
                </p>
                <div className="space-y-3">
                  {extraServices.map((extra) => (
                    <div
                      key={extra.category}
                      className="rounded-lg border border-blue-100 bg-blue-50 p-4"
                    >
                      <p className="font-medium text-blue-900">
                        {categoryLabel(extra.category)}
                      </p>
                      <p className="mt-1 text-sm text-blue-800/80">
                        {extra.note?.trim() || t('requestDetail.noNote')}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Inspection Report */}
          {request.inspection_report_url && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileImage className="mr-2 h-5 w-5" />
                  {t('requestDetail.inspectionReport')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <a
                    href={request.inspection_report_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block relative group cursor-pointer"
                  >
                    <img
                      src={request.inspection_report_url}
                      alt={t('requestDetail.inspectionReport')}
                      className="w-full h-auto max-h-96 object-contain rounded-lg border border-gray-200"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-lg" />
                  </a>
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-500">
                      {t('requestDetail.clickImageFullSize')}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(request.inspection_report_url, '_blank')}
                    >
                      {t('requestDetail.openImage')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Issues */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertTriangle className="mr-2 h-5 w-5" />
                {t('requestDetail.reportedIssues')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {request.inspection_issues && request.inspection_issues.length > 0 ? (
                <div className="space-y-4">
                  {request.inspection_issues.map((issue: any) => (
                    <div
                      key={issue.id}
                      className="p-4 bg-gray-50 rounded-lg border-l-4 border-yellow-400"
                    >
                      <h4 className="font-medium">{issue.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {issue.description}
                      </p>
                      {issue.severity && (
                        <span className="inline-block mt-2 text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                          {issue.severity}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">
                  {t('requestDetail.noIssues')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Description */}
          {request.description && (
            <Card>
              <CardHeader>
                <CardTitle>{t('requestDetail.description')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">{request.description}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column - Quotes & Actions */}
        <div className="space-y-6">
          {/* Action Card */}
          {(request.status === 'open' || existingQuote?.editable) && !request.cancelled && (
            <Card>
              <CardHeader>
                <CardTitle>{t('requestDetail.actions')}</CardTitle>
              </CardHeader>
              <CardContent>
                {existingQuote ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center text-green-700">
                        <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium">{t('requestDetail.quoteSent')}</span>
                      </div>
                      <p className="text-sm text-green-600 mt-1">
                        {t('requestDetail.alreadySentQuote', { price: formatCurrency(existingQuote.price) })}
                      </p>
                    </div>
                    {existingQuote.editable && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={openQuoteModal}
                      >
                        <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        {t('requestDetail.updateQuote')}
                      </Button>
                    )}
                  </div>
                ) : (
                  <Button
                    className="w-full"
                    onClick={openQuoteModal}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {t('requestDetail.sendQuote')}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Existing Quotes */}
          <Card>
            <CardHeader>
              <CardTitle>{t('requestDetail.yourQuotes')}</CardTitle>
            </CardHeader>
            <CardContent>
              {allQuotes.length > 0 ? (
                <div className="space-y-4">
                  {allQuotes.map((quote: Quote) => (
                      <div
                        key={quote.id}
                        className="p-4 border rounded-lg"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-2xl font-bold text-blue-600">
                              {formatCurrency(quote.calculated_total || quote.price)}
                            </p>
                            <p className="text-xs text-gray-500">{t('requestDetail.inclVat')}</p>
                            <StatusBadge status={quote.status} />
                          </div>
                        </div>
                        {/* Line Items */}
                        {quote.line_items && quote.line_items.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <p className="text-sm font-medium text-gray-700">{t('requestDetail.workOrderColon')}</p>
                            {quote.line_items.map((item: QuoteLineItem) => (
                              <div key={item.id} className="flex justify-between text-sm text-gray-600">
                                <span>{item.description}</span>
                                <span>{item.quantity} {item.unit_type} × {formatCurrency(item.unit_price)}</span>
                              </div>
                            ))}
                            {quote.tax_breakdown ? (
                              <div className="pt-2 border-t border-gray-200">
                                <TaxBreakdownSummary breakdown={quote.tax_breakdown} compact />
                              </div>
                            ) : null}
                          </div>
                        )}
                        {quote.message && (
                          <p className="text-sm text-gray-600 mt-2">
                            {quote.message}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          {t('requestDetail.sentAt', { date: formatDateTime(quote.created_at) })}
                        </p>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500">{t('requestDetail.noQuotesSent')}</p>
                  {request.status === 'open' && (
                    <p className="text-sm text-gray-400 mt-1">
                      {t('requestDetail.clickSendQuoteHint')}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quote Modal */}
      <Modal
        isOpen={showQuoteModal}
        onClose={() => {
          setShowQuoteModal(false)
          setSubmitError('')
        }}
        title={existingQuote ? t('requestDetail.updateQuote') : t('requestDetail.sendQuote')}
        size="lg"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setShowQuoteModal(false)
                setSubmitError('')
              }}
            >
              {t('requestDetail.cancel')}
            </Button>
            <Button
              onClick={handleSubmitQuote}
              isLoading={submitQuoteMutation.isPending}
              disabled={lineItems.length === 0 && !quotePrice}
            >
              {existingQuote ? t('requestDetail.update') : t('requestDetail.sendQuote')}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmitQuote}>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {submitError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {submitError}
              </div>
            )}

            {extraServices.length > 0 && !existingQuote && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                {t('requestDetail.extraServicesPrefilled')}
              </div>
            )}

            <WorkOrderEditor items={lineItems} onChange={setLineItems} addRowAtBottom />

            {/* Fallback: Simple Price Input (when no line items) */}
            {lineItems.length === 0 && (
              <>
                <Input
                  label={t('requestDetail.priceLabel', { currency: uiCurrency() })}
                  type="number"
                  placeholder={t('requestDetail.pricePlaceholder')}
                  value={quotePrice}
                  onChange={(e) => setQuotePrice(e.target.value)}
                  required={lineItems.length === 0}
                />
                {priceTaxBreakdown && (parseInt(quotePrice, 10) || 0) > 0 ? (
                  <TaxBreakdownSummary breakdown={priceTaxBreakdown} />
                ) : null}
              </>
            )}

            <Textarea
              label={t('requestDetail.messageLabel')}
              placeholder={t('requestDetail.messagePlaceholder')}
              value={quoteMessage}
              onChange={(e) => setQuoteMessage(e.target.value)}
              rows={3}
            />
          </div>
        </form>
      </Modal>
    </div>
  )
}
